/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useDropzone } from "react-dropzone";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import * as React from "react";
import "react-image-crop/dist/ReactCrop.css";
import ProgressButton from "./ProgressButton";
import { Button, CircularProgress, Paper } from "@mui/material";
import assetsService from "../../../service/assets.service";
import { uniqBy } from "lodash";
import { useTranslation } from "react-i18next";
import { formatFileSize } from "../../../helpers/utils";
import { useAppContext } from "../../../contexts/useStorageContext";
import { UserRoles } from "../../../types/auth";
import * as XLSX from "xlsx";
import { showSnackbar } from "../../../service/snackbar.service";
export enum AwsLocation {
  PrivateAssets = "private-assets",
  PublicAssets = "public-assets",
}

export const AwsLocationToBaseUrl = {
  [AwsLocation.PrivateAssets]:
    process.env.REACT_APP_DATA_LAKE_ASSET_BUCKET_BASE_URL,
  [AwsLocation.PublicAssets]:
    process.env.REACT_APP_INTAKE_ASSET_BUCKET_BASE_URL,
};

export enum AwsFileDirectory {
  Documentation = "documentation",
  SpectraCalibration = "spectra_calibration",
  SensorCalibration = "sensor_calibration",
  ResearchSchedule = "research_schedule",
  LabDevice = "lab_device",
  LabDeviceCalibration = "lab_device_calibration",
  GasAnalysisSource = "gas_analysis_source",
  GasAnalysisResult = "gas_analysis_result",
  PredictionModel = "prediction_model",
  DairyRation = "dairy_ration",
  OnboardingGuide = "onboarding_guide",
}

export interface FileInfo {
  name: string;
  size: number;
  blob: Blob;
  rowsData?: any[][]; // Parsed rows data for XLSX/CSV files
}
export interface AnyAssetFileUploaderProps {
  open: boolean;
  onClose: () => void;
  onSave: (urls: string[], sizes: number[]) => void;
  title?: string;
  ratio?: number;
  multiple?: boolean;
  location: AwsLocation;
  directory: string;
  companyId?: string;
  onFileParsed?: (fileName: string, rowsData: any[][]) => void; // Optional callback when file is parsed
  bottomContent?: React.ReactNode;
  expectedFileExtensions?: string[];
}

export default function AnyAssetFileUploader({
  open,
  onClose,
  onSave,
  title,
  multiple,
  location,
  directory,
  companyId,
  onFileParsed,
  bottomContent,
  expectedFileExtensions,
}: AnyAssetFileUploaderProps) {
  const { t } = useTranslation();
  const { state } = useAppContext();
  const { loggedInUserInfo } = state;
  const baseUrl = AwsLocationToBaseUrl[location || AwsLocation.PublicAssets];
  const [files, setFiles] = React.useState<FileInfo[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [checkingExistence, setCheckingExistence] = React.useState(false);
  const [existingFiles, setExistingFiles] = React.useState<
    { key: string; size: number }[]
  >([]);
  const [acceptedExistingFiles, setAcceptedExistingFiles] = React.useState<
    { key: string; size: number }[]
  >([]);

  const targetCompanyId = React.useMemo(() => {
    if (loggedInUserInfo?.role == UserRoles.superadmin) {
      return companyId || "";
    } else {
      return loggedInUserInfo?.company?._id || "";
    }
  }, [companyId, loggedInUserInfo]);

  const isExcelOrCsvFile = (fileName: string): boolean => {
    const lowerName = fileName.toLowerCase();
    return (
      lowerName.endsWith(".xlsx") ||
      lowerName.endsWith(".xls") ||
      lowerName.endsWith(".csv")
    );
  };

  const parseFileToRows = async (file: File): Promise<any[][] | undefined> => {
    try {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            if (!e?.target?.result) {
              resolve(undefined);
              return;
            }
            const data = e.target.result;
            const fileName = file.name.toLowerCase();

            let workbook: XLSX.WorkBook;

            if (fileName.endsWith(".csv")) {
              // Parse CSV file using XLSX library
              workbook = XLSX.read(data, {
                type: "binary",
                raw: false,
              });
            } else if (
              fileName.endsWith(".xlsx") ||
              fileName.endsWith(".xls")
            ) {
              // Parse XLSX/XLS file
              workbook = XLSX.read(data, {
                type: "binary",
              });
            } else {
              resolve(undefined);
              return;
            }

            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(sheet, {
              header: 1,
              defval: "",
            });
            resolve(jsonData as any[][]);
          } catch (error) {
            console.error("Error parsing file:", error);
            resolve(undefined);
          }
        };
        reader.onerror = () => {
          resolve(undefined);
        };

        // Read as binary string for both CSV and Excel files (XLSX library handles both)
        reader.readAsBinaryString(file);
      });
    } catch (error) {
      console.error("Error in parseFileToRows:", error);
      return undefined;
    }
  };

  const checkFileExistence = async (
    files: { fileName: string; size: number }[]
  ) => {
    try {
      setCheckingExistence(true);
      const existingFiles = await assetsService.checkFileExistence({
        files,
        location: location || AwsLocation.PublicAssets,
        companyId: targetCompanyId,
      });
      setCheckingExistence(false);
      setExistingFiles(existingFiles);
    } catch (error) {
      setCheckingExistence(false);
    }
  };

  const onFilesLoad = React.useCallback(
    async (acceptedFiles: any[]) => {
      try {
        const filePromises = acceptedFiles.map(async (file) => {
          const fileName = file?.name || "";
          return new Promise<FileInfo>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
              try {
                if (!reader.result) {
                  showSnackbar("danger", "", "The file could not be read.");
                  reject("Reader result is empty");
                  return;
                }
                const blob = new Blob([reader.result], { type: file.type });
                if (
                  expectedFileExtensions &&
                  !expectedFileExtensions.some((ext) =>
                    fileName.toLowerCase().endsWith(ext.toLowerCase())
                  )
                ) {
                  showSnackbar(
                    "danger",
                    "Error",
                    "This file type is not supported."
                  );
                  reject(`File extension is not supported`);
                  return;
                }
                // Parse rows data if it's an Excel or CSV file
                let rowsData: any[][] | undefined;
                if (onFileParsed && isExcelOrCsvFile(fileName)) {
                  rowsData = await parseFileToRows(file);
                  if (rowsData && onFileParsed) {
                    onFileParsed(fileName, rowsData);
                  }
                }

                resolve({
                  name: fileName,
                  size: file.size,
                  blob,
                });
              } catch (error) {
                reject(error);
              }
            };
            reader.readAsArrayBuffer(file);
          });
        });

        const tempFiles = await Promise.all(filePromises);
        let updatedFiles;
        if (multiple) {
          updatedFiles = uniqBy([...files, ...tempFiles], (v) => v.name);
        } else {
          updatedFiles = tempFiles;
        }
        setFiles(updatedFiles);
        checkFileExistence(
          updatedFiles.map((f) => ({ fileName: f.name, size: f.size }))
        );
      } catch (error) {
        console.error("Error processing files:", error);
      }
    },
    [files, onFileParsed]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onFilesLoad,
    multiple: multiple,
  });

  const uploadFile = async () => {
    if (!files?.length) return;
    const acceptedFiles = acceptedExistingFiles.filter((f) =>
      files.some(
        (file) =>
          f.key.endsWith(file.name.replaceAll(" ", "_")) && f.size == file.size
      )
    );
    const filesToUpload = files.filter(
      (file) =>
        !acceptedFiles.some(
          (f) =>
            f.key.endsWith(file.name.replaceAll(" ", "_")) &&
            f.size == file.size
        )
    );
    setUploading(true);
    const URLs = await Promise.all(
      filesToUpload.map((f) =>
        assetsService.uploadFile({
          blob: f.blob,
          fileName: f.name,
          location,
          directory,
          companyId: targetCompanyId,
        })
      )
    );

    const acceptedFileUrls = acceptedFiles.map(
      (f) => `${baseUrl}${baseUrl?.endsWith("/") ? "" : "/"}${f.key}`
    );
    onSave(
      [...(URLs || []), ...(acceptedFileUrls || [])],
      [
        ...(filesToUpload.map((f) => f.size) || []),
        ...(acceptedFiles.map((f) => f.size) || []),
      ]
    );
    setUploading(false);
  };

  return (
    <Dialog open={open} maxWidth={false}>
      <Box
        p={2}
        sx={{ maxWidth: "100%", minWidth: "450px" }}
      >
        <Box pb={1} sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">{title || t("Upload File")}</Typography>
          <IconButton onClick={onClose} sx={{ marginTop: "-10px" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box key={files.length}>
          <Box
            sx={{
              maxWidth: "500px",
            }}
          >
            {files.map((file, i) => {
              const existingMatchedFile = existingFiles.find(
                (existingFile) =>
                  existingFile.size == file.size &&
                  existingFile.key?.endsWith(file.name?.replaceAll(" ", "_"))
              );
              const isExistingFileAccepted =
                existingMatchedFile &&
                acceptedExistingFiles.find(
                  (acceptedFile) => acceptedFile.key == existingMatchedFile?.key
                );
              return (
                <Paper
                  key={i}
                  sx={{
                    flex: 1,
                    padding: "10px 20px",
                    border: "solid 1px #eee",
                    mb: 1,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Box mr={2}>
                      <FilePresentIcon
                        sx={{
                          fontSize: "50px",
                          opacity: "0.6",
                          marginLeft: "-7px",
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ mb: "5px" }}>{file.name}</Box>
                      <Box sx={{ fontSize: "14px", opacity: "0.8" }}>{`${t(
                        "Size"
                      )}: ${formatFileSize(file.size)}`}</Box>
                      {file.rowsData && (
                        <Box
                          sx={{
                            fontSize: "14px",
                            opacity: 0.8,
                            color: "success.main",
                            mt: 0.5,
                          }}
                        >
                          {`${file.rowsData.length} rows parsed`}
                        </Box>
                      )}
                      {checkingExistence ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mt: 1,
                            fontSize: "14px",
                            opacity: 0.7,
                            color: "warning.main",
                          }}
                        >
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          {t("Checking existence...")}
                        </Box>
                      ) : null}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconButton
                        onClick={() => {
                          setFiles(files.filter((f) => f.name != file.name));
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </div>
                  {existingMatchedFile ? (
                    <div style={{ fontSize: "14px" }}>
                      {isExistingFileAccepted ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color: "success.main",
                          }}
                        >
                          <CheckCircleIcon sx={{ mr: "4px" }} />
                          <span style={{ marginRight: "8px" }}>
                            {t("Accepted to use existing file")}
                          </span>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setAcceptedExistingFiles(
                                acceptedExistingFiles.filter(
                                  (f) =>
                                    !(
                                      f.key == existingMatchedFile.key &&
                                      f.size == existingMatchedFile.size
                                    )
                                )
                              )
                            }
                            disableElevation
                            sx={{
                              borderRadius: "100px",
                              minHeight: "unset",
                              maxHeight: "24px",
                            }}
                            color="warning"
                          >
                            {t("Cancel")}
                          </Button>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color: "warning.main",
                          }}
                        >
                          <ErrorIcon sx={{ mr: "4px" }} />
                          <span style={{ marginRight: "8px" }}>
                            {t(
                              "File already existing with same name, same size"
                            )}
                          </span>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              const updatedAcceptedExistingFiles = [
                                ...acceptedExistingFiles,
                                existingMatchedFile,
                              ];

                              if (
                                !files.some(
                                  (file) =>
                                    !updatedAcceptedExistingFiles.some(
                                      (f) =>
                                        f.key?.endsWith(
                                          file.name?.replaceAll(" ", "_")
                                        ) && f.size == file.size
                                    )
                                )
                              ) {
                                const urls = updatedAcceptedExistingFiles.map(
                                  (f) =>
                                    `${baseUrl}${
                                      baseUrl?.endsWith("/") ? "" : "/"
                                    }${f.key}`
                                );
                                onSave(
                                  urls,
                                  updatedAcceptedExistingFiles.map(
                                    (f) => f.size
                                  )
                                );
                              } else {
                                setAcceptedExistingFiles(
                                  updatedAcceptedExistingFiles
                                );
                              }
                            }}
                            disableElevation
                            sx={{
                              borderRadius: "100px",
                              minHeight: "unset",
                              maxHeight: "24px",
                            }}
                            color="success"
                          >
                            {t("Use it")}
                          </Button>
                        </Box>
                      )}
                    </div>
                  ) : null}
                </Paper>
              );
            })}
          </Box>
          {multiple || !files.length ? (
            <Box>
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <Box
                  sx={{
                    border: "dashed 2px",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark" ? "#2c3a55" : "#ddd",
                    borderRadius: "5px",
                    height: files.length ? "50px" : "300px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    opacity: "0.8",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#152033" : "#f3f3f3",
                  }}
                >
                  {t("Click here to add local file")}
                </Box>
              </div>
            </Box>
          ) : null}
          {bottomContent ? bottomContent : null}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              sx={{ mr: 1 }}
              onClick={() => {
                setFiles([]);
                setExistingFiles([]);
                setAcceptedExistingFiles([]);
              }}
            >
              {t("Cancel All Files")}
            </Button>
            <ProgressButton
              disabled={uploading || !files?.length || checkingExistence}
              inProgress={uploading}
              onClick={() => uploadFile()}
              title={t("Upload")}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
